import axios from 'axios';
import qs from 'query-string';
import FileSaver from 'file-saver';
import { get } from 'lodash';

import store from '../redux/store';
import {
  exchangeAccessToken,
  resetAuthentication,
  verifyOTP,
} from '../authentication/actions';
import { EnumAppType, EnumClientType } from '../constants/enum';
import { setGeneralError } from '../layout/components/UniversalModal/actions';
import { isEmpty } from './utils';
import { trans } from '../localization';
import {
  DEFAULT_FILE_NAME,
  ERROR_ACCESS_TOKEN_CODE,
  ERROR_FORCE_VERIFY_ACCOUNT,
  ERROR_PARAMS,
  ERROR_ACCOUNT_DELETED,
  SUCCESS_RESPONSE,
} from '../constants/constants';
import { stopAppLoading } from '../layout/actions';
import { addSecurityRequestHeader } from './interceptors';

const SUCCESSFUL_RESPONSE = {
  reply: {
    metadata: {},
  },
  result: SUCCESS_RESPONSE,
};

class Request {
  static token(targetApp = EnumAppType.NOW_MERCHANT_APP) {
    const globalToken = store.getState().auth.token;
    if (targetApp === EnumAppType.NOW_MERCHANT_APP) {
      return globalToken;
    }
    const objToken = store.getState().auth.tokenExtend[targetApp];
    return !isEmpty(objToken) ? objToken.token : globalToken;
  }

  static header(targetApp = EnumAppType.NOW_MERCHANT_APP) {
    return {
      'x-foody-client-id': '0',
      'x-foody-client-type': EnumClientType.CLIENT_WEB,
      'x-foody-app-type': targetApp,
      'x-foody-client-version': CONFIG.CLIENT_VERSION.API,
      'x-foody-api-version': 1,
      'x-foody-client-language': store.getState().setting.language,
      'x-foody-access-token': Request.token(targetApp),
    };
  }

  static shouldExchangeToken() {
    const stateAuth = store.getState().auth;
    if (!stateAuth.authenticated) {
      return false;
    }

    const now = new Date().getTime() / 1000;
    const { authenticatedTs } = stateAuth;
    const intervalExchangeTime =
      // eslint-disable-next-line no-mixed-operators
      authenticatedTs + CONFIG.EXCHANGE_TOKEN_INTERVAL_TIME * 3600;
    return now > stateAuth.expiryTs || now > intervalExchangeTime;
  }

  static resolveResponse(
    exchangeTokenWhenFail,
    fetchData,
    targetApp = EnumAppType.NOW_MERCHANT_APP,
  ) {
    return respAxios => {
      const { data: response } = respAxios;
      if (
        response.reply === null &&
        response.result === ERROR_ACCESS_TOKEN_CODE
      ) {
        if (exchangeTokenWhenFail) {
          return store.dispatch(exchangeAccessToken(fetchData, targetApp));
        }
        store.dispatch(resetAuthentication());
      } else if (response.result === ERROR_FORCE_VERIFY_ACCOUNT) {
        return store.dispatch(verifyOTP());
      } else if (response.result === ERROR_PARAMS) {
        store.dispatch(setGeneralError(ERROR_PARAMS));
      }
      return response;
    };
  }

  static resolveResponseAuth(respAxios) {
    const { data: response } = respAxios;
    if (
      response.reply === null &&
      response.result !== 'success' &&
      response.result !== 'error_auth' &&
      response.result !== 'error_force_logout'
    ) {
      store.dispatch(resetAuthentication());

      if (response.result === ERROR_ACCOUNT_DELETED) {
        store.dispatch(setGeneralError(trans('error_account_deleted')));
      }
    }
    return response;
  }

  static resolveResponseGoogleApi(respAxios) {
    const { data: response } = respAxios;
    return response;
  }

  static resolveError(error) {
    if (error.response.status === 401) {
      store.dispatch(resetAuthentication());
    } else if (error.response.status === 500) {
      store.dispatch(setGeneralError('general_api_error'));
      store.dispatch(stopAppLoading());
    }
    throw error;
  }

  static postMerchant(endpoint, data = {}) {
    function fetchData(exchangeTokenWhenFail = true) {
      const restaurantId = get(
        store.getState(),
        'setting.currentRestaurant.restaurant_id',
        0,
      );

      return axios
        .post(CONFIG.API_MERCHANT + endpoint, data, {
          headers: {
            ...Request.header(),
            'x-foody-selected-restaurant': restaurantId,
          },
        })
        .then(Request.resolveResponse(exchangeTokenWhenFail, fetchData))
        .catch(Request.resolveError);
    }

    if (Request.shouldExchangeToken()) {
      return store.dispatch(exchangeAccessToken(fetchData));
    }
    return fetchData();
  }

  static getMerchant(endpoint, params = {}) {
    function fetchData(exchangeTokenWhenFail = true) {
      const restaurantId = get(
        store.getState(),
        'setting.currentRestaurant.restaurant_id',
        0,
      );

      return axios
        .get(CONFIG.API_MERCHANT + endpoint, {
          params,
          headers: {
            ...Request.header(),
            'x-foody-selected-restaurant': restaurantId,
          },
        })
        .then(Request.resolveResponse(exchangeTokenWhenFail, fetchData))
        .catch(Request.resolveError);
    }
    if (Request.shouldExchangeToken()) {
      return store.dispatch(exchangeAccessToken(fetchData));
    }
    return fetchData();
  }

  static postNowShip(endpoint, data = {}) {
    function fetchData(exchangeTokenWhenFail = true) {
      return axios
        .post(CONFIG.API_NOW_SHIP + endpoint, data, {
          headers: Request.header(),
        })
        .then(Request.resolveResponse(exchangeTokenWhenFail, fetchData))
        .catch(Request.resolveError);
    }

    if (Request.shouldExchangeToken()) {
      return store.dispatch(exchangeAccessToken(fetchData));
    }
    return fetchData();
  }

  static getNowShip(endpoint, params = {}) {
    function fetchData(exchangeTokenWhenFail = true) {
      return axios
        .get(CONFIG.API_NOW_SHIP + endpoint, {
          params,
          headers: Request.header(),
        })
        .then(Request.resolveResponse(exchangeTokenWhenFail, fetchData))
        .catch(Request.resolveError);
    }
    if (Request.shouldExchangeToken()) {
      return store.dispatch(exchangeAccessToken(fetchData));
    }
    return fetchData();
  }

  static postAuth(endpoint, data = {}) {
    return axios
      .post(CONFIG.AUTH_ROOT + endpoint, data, {
        headers: Request.header(),
      })
      .then(Request.resolveResponseAuth)
      .catch(Request.resolveError);
  }

  static getNow(endpoint, params = {}) {
    function fetchData(exchangeTokenWhenFail = true) {
      return axios
        .get(CONFIG.API_NOW + endpoint, {
          params,
          headers: Request.header(),
        })
        .then(Request.resolveResponse(exchangeTokenWhenFail, fetchData))
        .catch(Request.resolveError);
    }
    if (Request.shouldExchangeToken()) {
      return store.dispatch(exchangeAccessToken(fetchData));
    }
    return fetchData();
  }

  // eslint-disable-next-line no-unused-vars
  static postTableNow(endpoint, data = {}) {
    return Promise.resolve(SUCCESSFUL_RESPONSE);
    // function fetchData(exchangeTokenWhenFail = true) {
    //   const targetApp = EnumAppType.NOW_TABLE_APP;
    //   return axios
    //     .post(CONFIG.API_TABLE_NOW + endpoint, data, {
    //       headers: Request.header(targetApp),
    //     })
    //     .then(
    //       Request.resolveResponse(exchangeTokenWhenFail, fetchData, targetApp),
    //     )
    //     .catch(Request.resolveError);
    // }
    // if (Request.shouldExchangeToken()) {
    //   return store.dispatch(exchangeAccessToken(fetchData));
    // }
    // return fetchData();
  }

  // eslint-disable-next-line no-unused-vars
  static getTableNow(endpoint, params = {}) {
    return Promise.resolve(SUCCESSFUL_RESPONSE);
    // function fetchData(exchangeTokenWhenFail = true) {
    //   const targetApp = EnumAppType.NOW_TABLE_APP;
    //   return axios
    //     .get(CONFIG.API_TABLE_NOW + endpoint, {
    //       params,
    //       headers: Request.header(targetApp),
    //     })
    //     .then(
    //       Request.resolveResponse(exchangeTokenWhenFail, fetchData, targetApp),
    //     )
    //     .catch(Request.resolveError);
    // }
    // if (Request.shouldExchangeToken()) {
    //   return store.dispatch(exchangeAccessToken(fetchData));
    // }
    // return fetchData();
  }

  static getGoogleAPI(endpoint, data = {}) {
    return axios
      .get(CONFIG.GOOGLE_API_ROOT + endpoint, {
        params: {
          ...data,
          key: CONFIG.GOOGLE_KEY,
        },
      })
      .then(Request.resolveResponseGoogleApi)
      .catch(Request.resolveError);
  }

  static deleteAccount() {
    return axios
      .get(`${CONFIG.API_MERCHANT}/api/user/delete_account`, {
        headers: Request.header(),
      })
      .then(Request.resolveResponseAuth)
      .catch(Request.resolveError);
  }
  static postPortal(endpoint, data = {}, headers) {
    function fetchData(exchangeTokenWhenFail = true) {
      const targetApp = EnumAppType.NOW_MERCHANT_APP;
      return axios
        .post(CONFIG.API_NOW_PORTAL + endpoint, data, {
          headers: {
            ...Request.header(targetApp),
            ...headers,
          },
        })
        .then(
          Request.resolveResponse(exchangeTokenWhenFail, fetchData, targetApp),
        )
        .catch(Request.resolveError);
    }

    if (Request.shouldExchangeToken()) {
      return store.dispatch(exchangeAccessToken(fetchData));
    }
    return fetchData();
  }

  static deletePortal(endpoint, params = {}) {
    function fetchData(exchangeTokenWhenFail = true) {
      const targetApp = EnumAppType.NOW_MERCHANT_APP;
      return axios
        .delete(CONFIG.API_NOW_PORTAL + endpoint, {
          params,
          headers: Request.header(targetApp),
        })
        .then(
          Request.resolveResponse(exchangeTokenWhenFail, fetchData, targetApp),
        )
        .catch(Request.resolveError);
    }

    if (Request.shouldExchangeToken()) {
      return store.dispatch(exchangeAccessToken(fetchData));
    }
    return fetchData();
  }

  static getPortal(endpoint, params = {}) {
    function fetchData(exchangeTokenWhenFail = true) {
      const targetApp = EnumAppType.NOW_MERCHANT_APP;

      return axios
        .get(CONFIG.API_NOW_PORTAL + endpoint, {
          params,
          headers: Request.header(targetApp),
        })
        .then(
          Request.resolveResponse(exchangeTokenWhenFail, fetchData, targetApp),
        )
        .catch(Request.resolveError);
    }
    if (Request.shouldExchangeToken()) {
      return store.dispatch(exchangeAccessToken(fetchData));
    }
    return fetchData();
  }

  static resolveResponseFile(
    exchangeTokenWhenFail,
    fetchData,
    targetApp = EnumAppType.NOW_MERCHANT_APP,
  ) {
    return response => {
      const { data } = response;
      const blobData = new Blob([response.data], {
        type: response.headers['content-type'],
      });
      // Check token
      const contentType =
        (response.headers && response.headers['content-type']) || '';
      if (contentType.includes('application/json')) {
        const decodedString = String.fromCharCode.apply(
          null,
          new Uint8Array(data),
        );
        const responseToJson = JSON.parse(decodedString);
        const { result } = responseToJson;
        if (result === ERROR_ACCESS_TOKEN_CODE) {
          if (exchangeTokenWhenFail) {
            return store.dispatch(exchangeAccessToken(fetchData, targetApp));
          }
          store.dispatch(resetAuthentication());
        } else if (result === ERROR_PARAMS) {
          store.dispatch(setGeneralError(ERROR_PARAMS));
        }
        return responseToJson;
      }

      // Get file name
      let fileName = DEFAULT_FILE_NAME;
      const disposition =
        (response.headers && response.headers['content-disposition']) || '';
      if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, '');
        }
      }

      // Save file export
      FileSaver.saveAs(blobData, fileName);

      return { result: SUCCESS_RESPONSE, reply: {} };
    };
  }

  // eslint-disable-next-line no-unused-vars
  static postTableNowFileData(endpoint, data = {}) {
    return Promise.resolve(SUCCESSFUL_RESPONSE);
    // function fetchData(exchangeTokenWhenFail = true) {
    //   const targetApp = EnumAppType.NOW_TABLE_APP;
    //   return axios
    //     .post(CONFIG.API_TABLE_NOW + endpoint, data, {
    //       headers: Request.header(targetApp),
    //       responseType: 'arraybuffer',
    //     })
    //     .then(
    //       Request.resolveResponseFile(
    //         exchangeTokenWhenFail,
    //         fetchData,
    //         targetApp,
    //       ),
    //     )
    //     .catch(Request.resolveError);
    // }
    // if (Request.shouldExchangeToken()) {
    //   return store.dispatch(exchangeAccessToken(fetchData));
    // }
    // return fetchData();
  }
}

axios.defaults.paramsSerializer = params =>
  qs.stringify(params, { sort: (a, b) => a.localeCompare(b) });
axios.interceptors.request.use(addSecurityRequestHeader);

export default Request;



// WEBPACK FOOTER //
// ./foody/src/utils/request.js